import React, { useState, useCallback, useMemo } from "react";
import { Button as MuiButton, Link as MuiLink, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import type { KeyedMutator } from "swr";
import { format } from "date-fns";
import { DataGrid } from "@/components/DataGrid/DataGrid";
import { EditNameModal } from "@/features/dmManagement/components/EditNameModal";
import { DesignPreviewModal } from "@/features/dmManagement/components/DesignPreviewModal";
import { RejectedReasonModal } from "@/features/dmManagement/components/RejectedReasonModal";
import { NoRowsOverlay } from "@/features/dmManagement/components/NoRowsOverlay";
import { PutDesignNameAPI } from "@/features/dmManagement/api/putDesignNameAPI";
import { useApiClient } from "@/hooks/useApiClient";
import type { Design } from "@/features/dmManagement/types";

const DesignNoRowsOverlay = (): JSX.Element => (
  <NoRowsOverlay message="デザインデータがありません" />
);

const dataGridInitialState = {
  sorting: { sortModel: [{ field: "createdAt", sort: "desc" as const }] },
};

const dataGridComponents = { NoRowsOverlay: DesignNoRowsOverlay } as const;

interface DesignListGridProps {
  designs: Design[];
  isLoading: boolean;
  mutate: KeyedMutator<Design[]>;
}

/**
 * デザイン一覧グリッド
 * デザインデータをDataGridで一覧表示し、識別名の編集やプレビュー機能を提供する
 */
export const DesignListGrid: React.FC<DesignListGridProps> = ({
  designs,
  isLoading,
  mutate,
}) => {
  const { apiClient } = useApiClient();
  const [page, setPage] = useState(0);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [rejectedReasonModalOpen, setRejectedReasonModalOpen] = useState(false);
  const [selectedDesign, setSelectedDesign] = useState<Design | null>(null);

  const handleEditName = useCallback((design: Design): void => {
    setSelectedDesign(design);
    setEditModalOpen(true);
  }, []);

  const handlePreview = useCallback((design: Design): void => {
    setSelectedDesign(design);
    setPreviewModalOpen(true);
  }, []);

  const handleOpenRejectedReason = useCallback((design: Design): void => {
    setSelectedDesign(design);
    setRejectedReasonModalOpen(true);
  }, []);

  const handleSaveName = useCallback(
    async (newName: string): Promise<void> => {
      if (!selectedDesign) return;
      await PutDesignNameAPI(apiClient, selectedDesign.id, {
        taskName: newName,
      });
      await mutate();
    },
    [apiClient, selectedDesign, mutate]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "taskId",
        headerName: "識別番号",
        width: 80,
        sortable: false,
      },
      {
        field: "taskName",
        headerName: "識別名",
        width: 200,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <>
            {params.value as string}
            <MuiLink
              component="button"
              sx={{ ml: 1 }}
              onClick={() => {
                handleEditName(params.row as Design);
              }}
            >
              編集
            </MuiLink>
          </>
        ),
      },
      {
        field: "productName",
        headerName: "仕様",
        width: 300,
        sortable: false,
      },
      {
        field: "createdAt",
        headerName: "申請日時",
        width: 180,
        renderCell: (params: GridRenderCellParams) => {
          const value = params.value as string;
          if (!value) return "";
          return format(new Date(value), "yyyy/MM/dd HH:mm");
        },
      },
      {
        field: "statusDisplay",
        headerName: "申請状況",
        width: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const design = params.row as Design;
          if (design.status !== "rejected") {
            return design.statusDisplay;
          }
          return (
            <Tooltip title="クリックすると却下理由を確認できます">
              <MuiLink
                component="button"
                underline="always"
                onClick={() => {
                  handleOpenRejectedReason(design);
                }}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {design.statusDisplay}
                <InfoOutlinedIcon fontSize="small" aria-hidden="true" />
              </MuiLink>
            </Tooltip>
          );
        },
      },
      {
        field: "action",
        headerName: "Action",
        width: 200,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const design = params.row as Design;
          return (
            <MuiButton
              variant="outlined"
              size="small"
              onClick={() => {
                handlePreview(design);
              }}
            >
              プレビュー
            </MuiButton>
          );
        },
      },
    ],
    [handleEditName, handlePreview, handleOpenRejectedReason]
  );

  return (
    <>
      <DataGrid
        stickyHeader
        rows={designs}
        columns={columns}
        loading={isLoading}
        page={page}
        pageSize={100}
        onPageChange={(newPage: number) => {
          setPage(newPage);
        }}
        rowsPerPageOptions={[100]}
        initialState={dataGridInitialState}
        components={dataGridComponents}
      />
      {selectedDesign && (
        <>
          <EditNameModal
            key={`edit-${selectedDesign.id}`}
            open={editModalOpen}
            onClose={() => {
              setEditModalOpen(false);
            }}
            currentName={selectedDesign.taskName}
            onSave={handleSaveName}
          />
          <DesignPreviewModal
            open={previewModalOpen}
            onClose={() => {
              setPreviewModalOpen(false);
            }}
            components={selectedDesign.components}
          />
          <RejectedReasonModal
            key={`reject-${selectedDesign.id}`}
            open={rejectedReasonModalOpen}
            onClose={() => {
              setRejectedReasonModalOpen(false);
            }}
            reason={selectedDesign.rejectedReason}
          />
        </>
      )}
    </>
  );
};
